import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ShieldAlert, ChevronRight, AlertTriangle, Check, Shield, Phone, Settings } from 'lucide-react';

export default function LossChaseDetector() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try { const res = await api.get('/tools/loss-chase/status'); setStatus(res.data); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, []);

  const levelConfig = {
    low: { color: '#10b981', bg: 'bg-[#10b981]/10', label: 'Low Risk', icon: Check, message: 'No concerning patterns detected. Keep playing responsibly.' },
    moderate: { color: '#f59e0b', bg: 'bg-[#f59e0b]/10', label: 'Moderate Risk', icon: Shield, message: 'Some patterns suggest you may be chasing losses. Consider setting stricter limits.' },
    severe: { color: '#ef4444', bg: 'bg-[#ef4444]/10', label: 'High Risk', icon: AlertTriangle, message: 'Concerning loss-chasing patterns detected. We strongly recommend taking a break.' },
  };

  const cfg = status ? levelConfig[status.level] || levelConfig.low : levelConfig.low;

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="loss-chase-detector-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Emotional Wellness</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Loss-Chase Detector</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#ef4444]/15 flex items-center justify-center shrink-0"><ShieldAlert className="h-6 w-6 text-[#ef4444]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Loss-Chase Detector</h1><p className="text-[#a0a8b8] text-sm mt-1">AI pattern recognition detecting escalating behavior with interventions</p></div>
      </div>

      {loading ? (
        <div className="ciq-card animate-pulse"><div className="h-32 bg-[#2d3548] rounded-lg" /></div>
      ) : status && (
        <div className="space-y-6 animate-fade-in">
          {/* Risk Score */}
          <div className="ciq-card text-center" style={{ borderColor: `${cfg.color}30` }}>
            <div className="relative w-32 h-32 mx-auto mb-4">
              <svg className="w-32 h-32 -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#252b3b" strokeWidth="3" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none"
                  stroke={cfg.color} strokeWidth="3" strokeDasharray={`${status.score}, 100`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-white">{status.score}</span>
                <span className="text-xs" style={{ color: cfg.color }}>{cfg.label}</span>
              </div>
            </div>
            <p className="text-sm text-[#a0a8b8] max-w-md mx-auto">{cfg.message}</p>
          </div>

          {/* Flags */}
          {status.flags.length > 0 && (
            <div className="ciq-card">
              <h3 className="text-white font-semibold text-sm mb-3">Detected Patterns</h3>
              <div className="space-y-2">
                {status.flags.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#ef4444]/5 border border-[#ef4444]/10" data-testid={`flag-${i}`}>
                    <AlertTriangle className="h-4 w-4 text-[#f59e0b] shrink-0" />
                    <p className="text-sm text-[#a0a8b8]">{f}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div className="ciq-card">
            <h3 className="text-white font-semibold text-sm mb-3">Protective Strategies</h3>
            <div className="space-y-2">
              {['Set a mandatory 30-minute wait between deposits', 'Use cash-out rules: withdraw 50% of any win immediately', 'Pre-commit to session limits before starting', 'Log your mood before playing using our Mood Tracker'].map((tip, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]"><Check className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" />{tip}</div>
              ))}
            </div>
          </div>

          {/* Support */}
          <div className="ciq-card border-[#ef4444]/20">
            <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><Phone className="h-4 w-4 text-[#ef4444]" /> Support Resources</h3>
            <p className="text-xs text-[#a0a8b8] mb-2">If you or someone you know has a gambling problem:</p>
            <div className="space-y-1 text-xs text-[#a0a8b8]">
              <p>National Gambling Helpline: <span className="text-[#4d7cfe]">1-800-522-4700</span></p>
              <p>Crisis Text Line: Text <span className="text-[#4d7cfe]">GAMBLING</span> to 741741</p>
              <p>BeGambleAware.org</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Link to="/emotional-wellness/mood-tracker" className="flex-1 h-10 rounded-lg bg-[#4d7cfe] text-white text-sm font-medium flex items-center justify-center gap-2 btn-lift">
              <ShieldAlert className="h-3.5 w-3.5" /> Log Mood Check-in
            </Link>
            <Link to="/viral-tools/betting-streak-tracker" className="flex-1 h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm flex items-center justify-center gap-2 hover:bg-white/5">
              View Session History
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
